import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import LandingPage from './pages/LandingPage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import Login from './pages/Login';
import RegisterAccount from './pages/RegisterAccount';
import VerifyEmailPage from './pages/VerifyEmailPage';
import RegistrationForm from './pages/RegistrationForm';
import ApplicantDashboard from './pages/ApplicantDashboard';
import ExamPage from './pages/ExamPage';
import AdminDashboard from './pages/AdminDashboard';
import VerifyVoucherPage from './pages/VerifyVoucherPage';
import { LanguageProvider } from './context/LanguageContext';
import './styles/App.css';
import './styles/DarkMode.css';
import './styles/Accessibility.css';
import './styles/AlphabetSwitcher.css';

export const ThemeContext = React.createContext({
  theme: 'light',
  toggleTheme: () => {}
});

export const useTheme = () => React.useContext(ThemeContext);

function AppContent({ user, setUser, token, setToken, theme, toggleTheme }) {
  const location = useLocation();
  const isExamActive = location.pathname.startsWith('/exam/');

  return (
    <div className="app-layout">
      {/* Hide navbar during examination to prevent distraction */}
      {!isExamActive && <Navbar user={user} setUser={setUser} setToken={setToken} theme={theme} toggleTheme={toggleTheme} />}

      <main className="main-content-wrapper">
        <Routes>
          <Route
            path="/"
            element={user && user.role === 'admin' ? <Navigate to="/admin" replace /> : <LandingPage user={user} />}
          />
          <Route path="/portal" element={<LandingPage user={user} />} />
          <Route path="/services/phd-exam" element={<ServiceDetailPage user={user} />} />
          <Route path="/service/:serviceId" element={<ServiceDetailPage user={user} />} />
          <Route path="/login" element={<Login user={user} setUser={setUser} setToken={setToken} />} />
          <Route path="/register-account" element={<RegisterAccount setUser={setUser} setToken={setToken} />} />
          <Route path="/verify-email" element={<VerifyEmailPage setUser={setUser} setToken={setToken} />} />
          <Route
            path="/register"
            element={
              user && user.role === 'admin' ? (
                <Navigate to="/admin" replace state={{ infoMessage: 'Administrator nomidan ariza topshirish cheklangan. Ariza topshirish faqat talabgorlar uchun moʻljallangan.' }} />
              ) : user ? (
                <RegistrationForm token={token} user={user} />
              ) : (
                <Navigate to="/login" replace state={{ infoMessage: 'Ariza topshirish uchun avval tizimga kiring.', returnUrl: '/register' }} />
              )
            }
          />
          <Route
            path="/dashboard"
            element={
              user && user.role === 'admin' ? (
                <Navigate to="/admin" replace />
              ) : user ? (
                <ApplicantDashboard user={user} token={token} />
              ) : (
                <Navigate to="/login" replace state={{ infoMessage: 'Shaxsiy kabinetga kirish uchun avval tizimga kiring.' }} />
              )
            }
          />
          <Route
            path="/exam/:type"
            element={user ? <ExamPage token={token} user={user} /> : <Navigate to="/login" replace state={{ infoMessage: 'Imtihon topshirish uchun avval tizimga kiring.' }} />}
          />
          <Route
            path="/admin"
            element={user && user.role === 'admin' ? <AdminDashboard token={token} user={user} /> : <Navigate to={user ? "/dashboard" : "/login"} replace state={{ infoMessage: 'Admin paneliga faqat portal maʼmuriyati xodimlari kirishi mumkin.', returnUrl: '/admin' }} />}
          />
          <Route path="/verify-voucher/:id" element={<VerifyVoucherPage />} />
          <Route path="/verify/voucher/:id" element={<VerifyVoucherPage />} />
        </Routes>
      </main>

      {!isExamActive && <Footer />}
    </div>
  );
}

function App() {
  const [token, setToken] = useState(() => localStorage.getItem('token') || null);
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem('user');
      if (!saved) return null;
      const parsed = JSON.parse(saved);
      if (parsed && parsed.name && (parsed.name.includes("Ilmiy boʻlim") || parsed.name.includes("Ilmiy boʻlim"))) {
        parsed.name = parsed.name.replace(/\s*\(Ilmiy bo['ʻ]lim\)/gi, ' (Portal maʼmuriyati)');
        localStorage.setItem('user', JSON.stringify(parsed));
      }
      return parsed;
    } catch (e) {
      return null;
    }
  });

  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('app_theme') || 'light';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.body.setAttribute('data-theme', theme);
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
    }
    localStorage.setItem('app_theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => (prev === 'dark' ? 'light' : 'dark'));
  };

  useEffect(() => {
    document.title = 'Turon universiteti | Interaktiv xizmatlar portali';
    const link = document.querySelector("link[rel*='icon']");
    if (link) {
      link.href = '/title-logo.png?v=3';
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <LanguageProvider>
        <Router>
          <AppContent user={user} setUser={setUser} token={token} setToken={setToken} theme={theme} toggleTheme={toggleTheme} />
        </Router>
      </LanguageProvider>
    </ThemeContext.Provider>
  );
}

export default App;
